import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  makeStyles,
  Link as MUILink,
} from '@material-ui/core'
import React from 'react'

import Markdown from '../Markdown'
import ContentfulImage from '@aletheia/common/components/ContentfulImage'
import { wrapDialogOnClose } from '@aletheia/common/utils/material-ui/dialog'
import { IPerson, IOrganization } from '@aletheia/eafunds/contentful'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2),
  },
  photo: {
    borderRadius: '50%',
  },
  photoWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  jobTitle: {
    fontFamily: theme.typography.h1.fontFamily,
    color: theme.palette.grey[700],
    marginBottom: theme.spacing(2),
  },
  organization: {
    fontFamily: theme.typography.h1.fontFamily,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  bodyMarkdown: {
    '& *': {
      fontFamily: theme.typography.h1.fontFamily,
      lineHeight: theme.typography.body2.lineHeight,
    },
  },
  subtitle: {
    marginTop: theme.spacing(2),
  },
}))

type TeamMemberDialogProps = {
  open: boolean
  onClose: () => void
  Person: IPerson
  jobTitle: React.ReactNode
  Organization?: IOrganization
  compact?: boolean
}

export const TeamMemberDialog: React.FC<TeamMemberDialogProps> = (props) => {
  const { Person, jobTitle, Organization, compact, ...dialogProps } = props
  const { title, body, photo, viewpoints, grantHistory } = Person.fields
  const classes = useStyles()

  const handleClose = wrapDialogOnClose(dialogProps.onClose)

  let organizationName
  if (Organization) {
    organizationName = (
      <Typography variant="h6" component="h2" className={classes.organization}>
        <MUILink
          target="_blank"
          rel="noopener"
          href={Organization.fields.website}
        >
          {Organization.fields.title}
        </MUILink>
      </Typography>
    )
  }

  return (
    <>
      <Dialog
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        {...dialogProps}
      >
        <DialogContent className={classes.root}>
          <Grid container spacing={5}>
            {!compact && (
              <Grid item xs={12} md={3} className={classes.photoWrapper}>
                <ContentfulImage
                  image={photo}
                  className={classes.photo}
                  widths={[190, 380]}
                  sizes="190px"
                  square
                />
              </Grid>
            )}
            <Grid item xs={12} md={compact ? 12 : 9}>
              <Typography variant="h5" component="h3" gutterBottom>
                {title}
              </Typography>
              {organizationName}
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                className={classes.jobTitle}
              >
                {jobTitle}
              </Typography>
              <Markdown className={classes.bodyMarkdown}>{body}</Markdown>
              {grantHistory && (
                <>
                  <Typography variant="h6" className={classes.subtitle}>
                    Grant History
                  </Typography>
                  <Markdown className={classes.bodyMarkdown}>
                    {grantHistory}
                  </Markdown>
                </>
              )}
              {viewpoints && (
                <>
                  <Typography variant="h6" className={classes.subtitle}>
                    Further reading
                  </Typography>
                  <Markdown className={classes.bodyMarkdown}>
                    {viewpoints}
                  </Markdown>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TeamMemberDialog
